import { ensureTrailingSlash } from '../text';
import { Router } from 'vue-router';

export function isExternalUrlMatch(url: URL, baseUrl: URL): boolean {
  return !url.href.startsWith(baseUrl.href);
}

function isParamsMatch(
  subjectParams: URLSearchParams,
  targetParams: URLSearchParams,
  exact = false,
) {
  if (exact) {
    const subjectParamsNormalized = new URLSearchParams(subjectParams);
    const targetParamsNormalized = new URLSearchParams(targetParams);
    subjectParamsNormalized.sort();
    targetParamsNormalized.sort();
    return subjectParamsNormalized.toString() === targetParamsNormalized.toString();
  }

  for (const key of subjectParams.keys()) {
    if (!targetParams.has(key) || targetParams.get(key) !== subjectParams.get(key)) return false;
  }
  return true;
}

export function normalizeUrl(url: URL): URL {
  // remove params and ensure trailing slash
  return new URL(`${url.origin}${ensureTrailingSlash(url.pathname)}`);
}

/**
 * Removes trailing slash from pathname (except for root "/")
 */
function removeTrailingSlash(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

export function isUrlMatch(subjectUrl: URL, targetUrl: URL, matchParams = false): boolean {
  // normalizing ensures that 'https://example.com/foo' doesn't match 'https://example.com/foo-bar'
  const subjectUrlNormalized = normalizeUrl(subjectUrl);
  const targetUrlNormalized = normalizeUrl(targetUrl);

  return (
    targetUrlNormalized.href.startsWith(subjectUrlNormalized.href) &&
    (matchParams ? isParamsMatch(subjectUrl.searchParams, targetUrl.searchParams, false) : true)
  );
}

export function isExactUrlMatch(
  subjectUrl: URL,
  targetUrl: URL,
  matchParams = false,
  exactParams = false,
): boolean {
  const testUrlNormalized = normalizeUrl(subjectUrl);
  const targetUrlNormalized = normalizeUrl(targetUrl);

  return (
    targetUrlNormalized.href === testUrlNormalized.href &&
    (matchParams
      ? isParamsMatch(subjectUrl.searchParams, targetUrl.searchParams, exactParams)
      : true)
  );
}

/**
 * Updates (adds or sets) a query parameter in the current URL without triggering a page reload.
 *
 * This function uses Vue Router's navigation system to update the query parameters,
 * which ensures that router guards are handled correctly and the application state
 * remains consistent. The navigation preserves all existing query parameters.
 *
 * @param router - The Vue Router instance
 * @param key - The query parameter key to set
 * @param value - The query parameter value to set
 * @returns The navigation; the URL has changed once it settles
 */
export function updateQueryParamInUrl(
  router: Router,
  key: string,
  value: string,
): Promise<unknown> {
  const currentPath = router.currentRoute.value.path;

  return router.replace({
    path: removeTrailingSlash(currentPath),
    query: {
      ...router.currentRoute.value.query,
      [key]: value,
    },
  });
}

/**
 * Removes a query parameter from the current URL without triggering a page reload.
 *
 * This function uses Vue Router's navigation system to remove query parameters,
 * which ensures that router guards are handled correctly and the application state
 * remains consistent. All other query parameters are preserved.
 *
 * @param router - The Vue Router instance
 * @param key - The query parameter key to remove
 */
export function removeQueryParamFromUrl(router: Router, key: string): void {
  const newQuery = { ...router.currentRoute.value.query };
  delete newQuery[key];

  router.replace({
    query: newQuery,
  });
}
